// Chuyển từ VUONGDATWOOD_V2/src/features/cong-luong/source-registry.ts
import { canonicalPayrollCode } from './domain';

function normalizeValues(values = {}) {
    return Object.fromEntries(Object.entries(values).map(([code, value]) => [canonicalPayrollCode(code), Number(value) || 0]));
}

function roundVnd(value) {
    return Math.round(Number.isFinite(value) ? value : 0);
}

function sumAdvances(advances = []) {
    return roundVnd(advances.reduce((sum, item) => sum + Math.max(0, item.amount), 0));
}

const isKilogram = (unit) => String(unit ?? '').trim().toLowerCase() === 'kg';
const teamKeyOf = (line) => String(line.TO ?? '').trim() || `#${line.TO_PBNS_ID ?? ''}`;

/**
 * Khối lượng kg theo từng tổ người này phụ trách. Mọi công đoạn của một tổ cùng tính trên khối lượng
 * bao bì của tổ (SANH phụ trách Cắt, Cưa, Lựa của CD2 đều 281.380 kg), nên mỗi tổ lấy công đoạn lớn
 * nhất chứ không cộng các công đoạn. Một công đoạn có thể tách nhiều dòng khi đổi đơn giá giữa tháng
 * nên cộng theo (tổ, công đoạn) trước. Dòng không phải kg (ví dụ Bo lồng tính theo cái) bị bỏ. Dòng
 * lưu từ trước khi có cột TO thì coi mỗi phân bổ là một tổ.
 */
export function teamWeightsFromLines(lines = []) {
    const byStage = new Map();
    for (const line of lines) {
        if (!isKilogram(line.DON_VI_TINH)) continue;
        const team = teamKeyOf(line);
        const key = `${team}|${line.MA_CONG_DOAN ?? line.TEN_CONG_DOAN ?? ''}`;
        const stage = byStage.get(key) ?? { team, weight: 0 };
        stage.weight += Number(line.KHOI_LUONG) || 0;
        byStage.set(key, stage);
    }
    const byTeam = new Map();
    for (const { team, weight } of byStage.values()) byTeam.set(team, Math.max(byTeam.get(team) ?? 0, weight));
    return Array.from(byTeam, ([team, weight]) => ({ team: team.startsWith('#') ? '' : team, weight: Number(weight.toFixed(6)) }))
        .sort((left, right) => left.team.localeCompare(right.team, 'vi', { numeric: true }));
}

export function teamWeightFromLines(lines = []) {
    return Number(teamWeightsFromLines(lines).reduce((sum, item) => sum + item.weight, 0).toFixed(6));
}

export const PAYROLL_VARIABLE_SOURCE_REGISTRY = {
    LSP: 'NANG_SUAT',
    KLNS: 'NANG_SUAT',
    SLL: 'NHAP_KY',
    TTN: 'NHAP_KY',
    TL_GL_PCTN: 'THOA_THUAN',
    DG_GL_KL: 'THOA_THUAN',
    DG_LONG: 'HE_THONG',
    PCTN_GL: 'CONG_THUC',
    PCTN_TL: 'CONG_THUC',
    KL_GL: 'CONG_THUC',
    GL: 'CONG_THUC',
    TLSP: 'CONG_THUC',
    PCL: 'CONG_THUC',
    NC: 'NHAP_KY',
    GTC: 'NHAP_KY',
    PCK: 'NHAP_KY',
    GK: 'NHAP_KY',
    DGN: 'THOA_THUAN',
    LK: 'THOA_THUAN',
    MLT: 'THOA_THUAN',
    NCC: 'THOA_THUAN',
    DGTC: 'THOA_THUAN',
    DGPCC: 'THOA_THUAN',
    PCTN: 'THOA_THUAN',
    PCNT: 'THOA_THUAN',
    PN: 'PHEP_NAM',
    NL: 'NGAY_LE',
    // Hằng số công ty: nguồn HE_THONG chung, đọc từ context.systemValues ở nhánh cuối.
    GIO_CC: 'HE_THONG',
    HSL_TC: 'HE_THONG',
    TL_BHXH_NLD: 'HE_THONG',
    TL_BHYT_NLD: 'HE_THONG',
    TL_BHTN_NLD: 'HE_THONG',
    TL_BHXH_DN: 'HE_THONG',
    TL_BHYT_DN: 'HE_THONG',
    TL_BHTN_DN: 'HE_THONG',
    TL_CD_DN: 'HE_THONG',
    MIEN_PCC: 'HE_THONG',
    GTBT: 'HE_THONG',
    GTNPT_1: 'HE_THONG',
    UNG: 'UNG_LUONG',
    // Bảo hiểm và thuế: mức lương đóng và số người phụ thuộc là thỏa thuận, các khoản tính ra là công thức.
    LDBH: 'THOA_THUAN',
    SO_NPT: 'THOA_THUAN',
    BHXH_NLD: 'CONG_THUC',
    BHYT_NLD: 'CONG_THUC',
    BHTN_NLD: 'CONG_THUC',
    BH_NLD: 'CONG_THUC',
    BHXH_DN: 'CONG_THUC',
    BHYT_DN: 'CONG_THUC',
    BHTN_DN: 'CONG_THUC',
    CD_DN: 'CONG_THUC',
    TNCT: 'CONG_THUC',
    GTNPT: 'CONG_THUC',
    TNTT: 'CONG_THUC',
    TNCN: 'CONG_THUC',
    NCT: 'CONG_THUC',
    LTH: 'CONG_THUC',
    LN: 'CONG_THUC',
    TTC: 'CONG_THUC',
    PCC: 'CONG_THUC',
    TC: 'CONG_THUC',
    TCHI: 'CONG_THUC',
};

export function getPayrollVariableSource(code, configuredSource) {
    return configuredSource ?? PAYROLL_VARIABLE_SOURCE_REGISTRY[canonicalPayrollCode(code)] ?? 'HE_THONG';
}

export function resolvePayrollSourceValues(codes, context, configuredSources = {}) {
    const manual = normalizeValues(context.manualValues);
    const agreement = normalizeValues(context.agreementValues);
    const system = normalizeValues(context.systemValues);
    const lines = context.productivityLines ?? [];
    const productAmount = lines.reduce((sum, line) => sum + line.THANH_TIEN, 0);
    const values = {};
    const traces = [];

    for (const rawCode of codes) {
        const code = canonicalPayrollCode(rawCode);
        const source = getPayrollVariableSource(code, configuredSources[code]);
        let value = 0;
        if (source === 'NANG_SUAT') {
            if (code === 'LSP') value = productAmount;
            if (code === 'KLNS') value = teamWeightFromLines(lines);
        } else if (source === 'NHAP_KY') {
            value = manual[code] ?? 0;
            if (code === 'NC') value = manual[code] ?? context.attendance?.workDays ?? 0;
            if (code === 'GTC') value = manual[code] ?? context.attendance?.overtimeHours ?? 0;
        } else if (source === 'THOA_THUAN') {
            value = manual[code] ?? agreement[code] ?? 0;
            if (code === 'DGN') value = manual[code] ?? agreement[code] ?? agreement.LN ?? 0;
            if (code === 'DGTC') value = manual[code] ?? context.attendance?.overtimeRate ?? 0;
        } else if (source === 'PHEP_NAM') {
            value = manual[code] ?? context.attendance?.paidLeaveDays ?? 0;
        } else if (source === 'NGAY_LE') {
            value = manual[code] ?? context.attendance?.paidHolidayDays ?? 0;
        } else if (source === 'UNG_LUONG') {
            value = sumAdvances(context.advances);
        } else if (source === 'BHXH_THUE' || source === 'HE_THONG') {
            value = system[code] ?? manual[code] ?? 0;
        }
        values[code] = value;
        traces.push({ code, source, sourceValue: value, dependencies: {}, calculatedValue: value });
    }

    return { values, traces };
}
